import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ListVideo, RotateCcw, Trash2 } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { StatusPill } from '@vnetwork/ui/data';
import { Button, Badge, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useState } from 'react';
import {
  useVodJobDetail,
  useVodStatuses,
  useRedoVodJob,
  useDeleteVodJob,
} from '@/api/media/vod-jobs';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';
import { JobDetailTimeline } from './job-detail/JobDetailTimeline';
import { JobDetailMediaCards } from './job-detail/JobDetailMediaCards';
import { JobDetailMasterUrlCard, type OutputGroup } from './job-detail/JobDetailMasterUrlCard';
import { JobDetailRuntimeCard } from './job-detail/JobDetailRuntimeCard';
import { JobDetailNotificationCallbackCard } from './job-detail/JobDetailNotificationCallbackCard';
import { jobStatusTone } from '../../shared/job-status-tone';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import styles from './JobDetail.module.css';

interface Props {
  serviceId: string;
}

const fmtDate = (raw?: string | null) => (raw ? new Date(raw).toLocaleString() : '—');

function bytesToMB(v?: number | string | null): string {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function hasValue(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

function calcDuration(startedAt?: string | null, completedAt?: string | null): string {
  if (!startedAt || !completedAt) return '—';
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '—';
  const sec = Math.floor((end - start) / 1000);
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ${sec % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function getIngestionTypeLabel(v?: string | number | null): string {
  const map: Record<string, string> = { '1': 'FTP', '2': 'HTTP', '5': 'SFTP', '6': 'WebDAV', '8': 'S3' };
  return map[String(v ?? '')] || String(v ?? '—');
}

function getDistributionTypeLabel(v?: string | number | null): string {
  const map: Record<string, string> = { '1': 'FTP', '3': 'CDN', '4': 'SFTP', '7': 'HTTPS', '8': 'S3' };
  return map[String(v ?? '')] || String(v ?? '—');
}

// source_type (spec): 1 = pulled from a storage/file, 2 = fetched from a URL.
function getSourceTypeLabel(v?: string | number | null): string | null {
  const map: Record<string, string> = { '1': 'File', '2': 'URL' };
  return map[String(v ?? '')] ?? (v != null ? String(v) : null);
}

export function VodJobDetail({ serviceId }: Props) {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const base = `/services/vod/${serviceId}`;

  const [deleteOpen, setDeleteOpen] = useState(false);

  const { data, isLoading, isError, error } = useVodJobDetail(serviceId, jobId ?? '');
  const { data: statuses = [] } = useVodStatuses(serviceId);

  const redo = useRedoVodJob(serviceId, {
    onSuccess: () => toast.success('Job restarted'),
    onError: () => toast.error('Failed to restart job'),
  });

  const remove = useDeleteVodJob(serviceId, {
    onSuccess: () => {
      toast.success('Job deleted');
      navigate(`${base}/jobs`);
    },
    onError: () => toast.error('Failed to delete job'),
  });

  const job = data?.job;
  const busy = redo.isPending || remove.isPending;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const metadata: any = job?.metadata ?? {};
  const jobResults: unknown[] = metadata?.job_results ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const jobMetadatas: any[] = metadata?.job_metadatas ?? [];
  // Explicit external subtitle tracks (added audios/subtitles), as a stable count
  // so the media useMemo doesn't rebuild on every render.
  const extSubtitleTracks: number = Array.isArray(metadata?.job_ext_subtitle_metadatas)
    ? metadata.job_ext_subtitle_metadatas.length
    : 0;
  const timeline: unknown[] = metadata?.job_statuses ?? [];
  const callbackConfig = metadata?.callback_config ?? null;

  const statusLabelMap = useMemo(
    () =>
      (statuses ?? []).reduce<Record<string, string>>((acc, s) => {
        if (s.value != null) acc[String(s.value)] = s.name;
        return acc;
      }, {}),
    [statuses],
  );

  const statusVal = String(job?.provider_status ?? job?.status ?? '');
  const statusLabel = statusLabelMap[statusVal] || (job?.status ?? 'Unknown');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const firstResult = (jobResults[0] as any) ?? {};
  const fileSize = bytesToMB(
    jobMetadatas.find((m) => Number(m?.metadata_dict_id) === 8)?.metadata_value,
  );
  const totalOutputSize = bytesToMB(firstResult?.storage);
  const masterUrl: string = firstResult?.master_url || data?.detail?.url || '';
  const showMasterUrl = hasValue(masterUrl);

  const ingestionConfig =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (job?.spec as any)?.ingestion_config ?? metadata?.ingestion_config ?? {};

  const pullFrom = [
    getIngestionTypeLabel(ingestionConfig?.ingestion_type),
    ingestionConfig?.file_path || null,
  ]
    .filter(hasValue)
    .join(' • ');

  const transcodingConfigs =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((job?.spec as any)?.transcoding_configs?.length
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (job?.spec as any).transcoding_configs
      : metadata?.transcoding_configs) ?? [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const distributeTo: string[] = (transcodingConfigs as any[])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .flatMap((c: any) => c?.distribution_configs ?? [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((c: any) =>
      [getDistributionTypeLabel(c?.distribution_type), c?.path || null]
        .filter(hasValue)
        .join(' • '),
    )
    .filter((v: string, i: number, a: string[]) => hasValue(v) && a.indexOf(v) === i);

  const errorMessage = job?.error || data?.detail?.error || metadata?.job_result?.error_message;

  const distributionCount = transcodingConfigs.reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (acc: number, c: any) => acc + (c?.distribution_configs?.length ?? 0),
    0,
  );

  const duration = calcDuration(job?.started_at, job?.completed_at);

  const media = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const visible = jobMetadatas.filter((m: any) => !m?.not_show);
    const findBy = (type: number, name: string) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      visible.find((m: any) => m?.type === type && String(m?.name ?? '').toLowerCase() === name.toLowerCase())
        ?.metadata_value;
    return {
      video: {
        codec: findBy(1, 'Codec'),
        resolution: findBy(1, 'Dimension'),
        fps: findBy(1, 'FPS'),
        bitrate: findBy(1, 'Bitrate'),
        aspectRatio: findBy(1, 'Aspect Ratio'),
      },
      audio: {
        codec: findBy(2, 'Codec'),
        sampleRate: findBy(2, 'Sample Rate'),
        bitrate: findBy(2, 'Bitrate'),
        channels: findBy(2, 'Channels'),
      },
      // Real subtitle tracks only: the vendor's explicit external-subtitle list,
      // plus an embedded subtitle when a type-3 property carries a real value.
      // (It emits "--" placeholder type-3 rows — Codec/Channels — even when the
      // source has no subtitle, so counting those rows overreports tracks.)
      subtitleCount:
        extSubtitleTracks +
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (visible.some((m: any) => m?.type === 3 && m?.metadata_value && m.metadata_value !== '--') ? 1 : 0),
    };
  }, [jobMetadatas, extSubtitleTracks]);

  const presetNameMap = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (metadata?.tc_presets ?? []).reduce((acc: Record<string, string>, p: any) => {
        if (p?.id != null) acc[String(p.id)] = p?.name ?? `Preset ${p.id}`;
        return acc;
      }, {}),
    [metadata?.tc_presets],
  );

  // profile_id → display name, from tc_presets[].profiles[] (job_result_profiles
  // carry a null profile_name, so the name must come from the preset catalog).
  const profileNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (metadata?.tc_presets ?? []).forEach((tp: any) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (tp?.profiles ?? []).forEach((p: any) => {
        if (p?.id != null) m[String(p.id)] = p?.name ?? `Profile ${p.id}`;
      });
    });
    return m;
  }, [metadata?.tc_presets]);

  // Group job_results by preset, then merge each profile's HLS + MP4 outputs into
  // one entry (so a profile name shows once). transcoding_format: 1 = HLS, 6 = MP4.
  const outputGroups = useMemo<OutputGroup[]>(() => {
    const order: string[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const byPreset: Record<string, { presetName: string; hlsManifest: string; profiles: Record<string, any>; profileOrder: string[] }> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (jobResults as any[]).forEach((r: any) => {
      const pid = String(r?.preset_id ?? '');
      if (!byPreset[pid]) {
        byPreset[pid] = { presetName: presetNameMap[pid] ?? `Preset ${pid}`, hlsManifest: '', profiles: {}, profileOrder: [] };
        order.push(pid);
      }
      const g = byPreset[pid];
      const isHls = r?.transcoding_format === 1;
      if (isHls && hasValue(r?.master_url)) g.hlsManifest = r.master_url;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (r?.job_result_profiles ?? []).forEach((p: any) => {
        const key = String(p?.profile_id ?? '');
        if (!g.profiles[key]) {
          g.profiles[key] = { name: profileNameMap[key] ?? `Profile ${key}` };
          g.profileOrder.push(key);
        }
        const url = p?.profile_url ?? '';
        if (!hasValue(url)) return;
        if (isHls) g.profiles[key].hls = url;
        else if (r?.transcoding_format === 6) g.profiles[key].mp4 = url;
      });
    });
    return order.map((pid) => ({
      presetName: byPreset[pid].presetName,
      hlsManifest: byPreset[pid].hlsManifest,
      profiles: byPreset[pid].profileOrder.map((k) => byPreset[pid].profiles[k]),
    }));
  }, [jobResults, presetNameMap, profileNameMap]);

  const canRedo = job?.status === 'failed';

  if (isLoading) {
    return (
      <Container size="xl">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Skeleton variant="rect" height={28} width={260} />
          <Skeleton variant="rect" height={120} />
        </div>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container size="xl">
        <Alert tone="danger" title="Failed to load job">
          {(error as Error)?.message ?? 'An unexpected error occurred.'}
        </Alert>
      </Container>
    );
  }

  if (!job) return null;

  const jobName = job.name || 'Untitled job';

  return (
    <Container size="xl">
      <PageHeader
        icon={<ListVideo size={20} />}
        breadcrumbs={
          <VodConsoleBreadcrumbs trail={[{ label: 'Jobs', slug: 'jobs' }, { label: jobName }]} />
        }
        title={
          <span className={styles.titleFlex}>
            <span className={styles.titleText}>{jobName}</span>
            <StatusPill tone={jobStatusTone(statusLabel)}>{statusLabel}</StatusPill>
          </span>
        }
        description={<>ID: <span className={styles.descriptionMono}>{job.id}</span></>}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<RotateCcw size={14} />}
              disabled={!canRedo || busy}
              onClick={() => job?.id && redo.mutate(String(job.id))}
            >
              {redo.isPending ? 'Restarting…' : 'Retry'}
            </Button>
            <Button
              variant="danger"
              size="sm"
              startIcon={<Trash2 size={14} />}
              disabled={busy}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </>
        }
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Error banner */}
      {hasValue(errorMessage) && (
        <Alert tone="danger" title="Job error">
          {String(errorMessage)}
        </Alert>
      )}

      {/* KPI stats — CompactStat inside a flat Card, matches the console detail-page pattern. */}
      <Section style={{ marginBottom: 0 }}>
        <Card flat>
          <CardBody>
            <CompactStatGroup columns={4}>
              <CompactStat label="File Size" value={fileSize} />
              <CompactStat label="Output Size" value={totalOutputSize} />
              <CompactStat label="Distribution" value={distributionCount} hint="targets" />
              <CompactStat label="Duration" value={duration} hint="started → completed" />
              <CompactStat label="Created" value={fmtDate(job.created_at)} />
              <CompactStat label="Started" value={fmtDate(job.started_at)} />
              <CompactStat label="Completed" value={fmtDate(job.completed_at)} />
              <CompactStat label="Retries" value={job.retry_count != null ? String(job.retry_count) : '0'} />
            </CompactStatGroup>
          </CardBody>
        </Card>
      </Section>

      {/* Master URL */}
      {showMasterUrl && (
        <JobDetailMasterUrlCard masterUrl={masterUrl} outputs={outputGroups} jobId={job.id ? String(job.id) : jobId} />
      )}

      {/* Runtime settings */}
      <JobDetailRuntimeCard
        priority={metadata?.priority}
        gpu={metadata?.use_gpu}
        // source_type lives in the submitted spec; metadata doesn't carry it.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        sourceType={getSourceTypeLabel((job?.spec as any)?.source_type ?? metadata?.source_type)}
        inputFile={ingestionConfig?.file_path}
        pullFrom={pullFrom}
        distributeTo={distributeTo}
      />

      {/* Preset badges */}
      {data?.detail?.preset && data.detail.preset.length > 0 && (
        <Section title="Preset Configuration" description="Output presets applied to this job." style={{ marginBottom: 0 }}>
          <div className={styles.presetList}>
            {(data.detail.preset as string[]).map((p, i) => (
              <Badge key={i} tone="neutral">{p}</Badge>
            ))}
          </div>
        </Section>
      )}

      {/* Media specs */}
      <JobDetailMediaCards media={media} />

      {/* Timeline */}
      <JobDetailTimeline timeline={timeline as never[]} statuses={statuses} />

      {/* Notification callback */}
      {callbackConfig && <JobDetailNotificationCallbackCard callbackConfig={callbackConfig} />}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete job?"
        description={`Permanently delete job "${job.name || job.id}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        requireText={job.name || String(job.id)}
        loading={remove.isPending}
        onConfirm={() => job.id && remove.mutate(String(job.id))}
      />
    </Container>
  );
}

export default VodJobDetail;
